import type { GameId } from "./index.js";

/**
 * Social objects (spec §8.9, §9): one link infrastructure for every public share. The URL carries only an opaque
 * token; the server resolves it. Types grow with their features (friend/crew invites, gifts come later).
 */
export const SOCIAL_OBJECT_TYPES = ["challenge", "seeded_level"] as const;
export type SocialObjectType = (typeof SOCIAL_OBJECT_TYPES)[number];

/** 18 random bytes, base64url: what `/s/:token` carries. Anything else resolves as not found. */
export const SOCIAL_TOKEN_PATTERN = /^[A-Za-z0-9_-]{24}$/;

export const CHALLENGE_OBJECTIVES = ["higher_score", "fewer_moves", "more_moves_left", "faster"] as const;
export type ChallengeObjective = (typeof CHALLENGE_OBJECTIVES)[number];

/** Where a shared link starts play: a level of the map, a seed (Harf Yılanı), or both; with the ruleset it ran on. */
export interface PlaySpot {
  level?: number;
  seed?: string;
  lang?: "tr" | "en";
  rulesetVersion: string;
}

/** "Same level, can you beat me?" The creator's result is a claim until replay verification (Phase 1C). */
export interface ChallengePayload extends PlaySpot {
  mode: string;
  objective: { type: ChallengeObjective };
  /** Small numeric result fields, e.g. `{ moves: 14 }`, `{ left: 6 }`, `{ score: 8420 }`. */
  creatorResult: Record<string, number>;
}

export type SeededLevelPayload = PlaySpot;

export type CreateSocialObjectRequest =
  | { type: "challenge"; gameId: GameId; payload: ChallengePayload }
  | { type: "seeded_level"; gameId: GameId; payload: SeededLevelPayload };

export interface CreateSocialObjectResponse {
  token: string;
  type: SocialObjectType;
  gameId: GameId;
  shareUrl: string;
  /** Null for long-lived links (seeded levels). Never shown to players as urgency. */
  expiresAt: string | null;
}

export interface ResolvedSocialObject {
  type: SocialObjectType;
  gameId: GameId;
  payloadVersion: number;
  payload: ChallengePayload | SeededLevelPayload;
  /** Public profile fields only: no user or database IDs. */
  creator: { displayName: string; avatarKey: string };
  createdAt: string;
  expiresAt: string | null;
}

/** `own`: the creator opened their own link (no attribution). `first`: this player's first start of the link. */
export interface StartSocialObjectResponse {
  status: "started" | "own";
  first: boolean;
}
